"use client";

import { Check } from "lucide-react";
import { BackAwareLink } from "@/components/ui/back-aware-link";
import { Drawer, DrawerContent, DrawerTitle } from "@/components/ui/drawer";
import { StickerBlob } from "@/components/wallet/sticker-blob";
import {
  STICKER_REGISTRY,
  stickerRequirement,
} from "@/components/wallet/wallet-stickers";
import { useMyStickers } from "@/hooks/use-stickers";
import { notificationLinks } from "@/lib/notification-links";
import { uiHaptics } from "@/lib/ui-haptics";
import { cn } from "@/lib/utils";

/**
 * Pick one of your own Hiive stickers to share. Only stickers you have
 * unlocked are listed — locked ones never appear, and the server refuses
 * anything else on publish.
 */
export function HiiveStickerPicker({
  open,
  onOpenChange,
  onSelect,
  selectedId = null,
  layer,
  nested = false,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (stickerId: string) => void;
  selectedId?: string | null;
  layer?: number;
  nested?: boolean;
}) {
  const mine = useMyStickers(open);
  const unlocked = new Set(mine.data?.unlockedIds ?? []);
  const stickers = STICKER_REGISTRY.filter((sticker) =>
    unlocked.has(sticker.id),
  );
  const lockedCount = STICKER_REGISTRY.length - stickers.length;

  return (
    <Drawer open={open} onOpenChange={onOpenChange} nested={nested}>
      <DrawerContent
        layer={layer}
        className="mx-auto max-h-[80dvh] max-w-lg data-[vaul-drawer-direction=bottom]:rounded-t-3xl"
      >
        <DrawerTitle className="px-5 pt-3 text-base font-semibold text-text-primary">
          Your Hiive stickers
        </DrawerTitle>
        <p className="px-5 pt-0.5 text-[13px] text-text-secondary">
          Only stickers you've unlocked can be shared.
        </p>
        <div className="min-h-0 flex-1 overflow-y-auto px-4 pt-3 pb-[max(env(safe-area-inset-bottom),1.25rem)]">
          {mine.isPending && mine.signedIn ? (
            <div className="grid grid-cols-3 gap-2" aria-busy="true">
              {Array.from({ length: 6 }, (_, index) => (
                <div
                  // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton tiles.
                  key={index}
                  className="h-[7.5rem] animate-pulse rounded-2xl bg-black/[0.05] dark:bg-white/[0.06]"
                />
              ))}
            </div>
          ) : mine.isError ? (
            <p className="py-8 text-center text-sm text-text-secondary">
              Couldn't load your stickers. Try again in a moment.
            </p>
          ) : stickers.length === 0 ? (
            <div className="flex flex-col items-center gap-3 py-8 text-center">
              <p className="text-sm text-text-secondary">
                You haven't unlocked a sticker yet.
              </p>
              <BackAwareLink
                href={notificationLinks.walletStickers()}
                onClick={() => onOpenChange(false)}
                className="rounded-full border border-border bg-surface px-4 py-2 text-sm font-semibold text-text-primary"
              >
                See how to unlock one
              </BackAwareLink>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-3 gap-2">
                {stickers.map((sticker) => {
                  const active = sticker.id === selectedId;
                  return (
                    <button
                      key={sticker.id}
                      type="button"
                      aria-pressed={active}
                      onClick={() => {
                        uiHaptics.select();
                        onSelect(sticker.id);
                        onOpenChange(false);
                      }}
                      className={cn(
                        "relative flex h-[7.5rem] flex-col items-center justify-center gap-1.5 rounded-2xl border px-1.5 text-center transition-[transform,background-color] duration-150 ease-out-strong active:scale-[0.96]",
                        active
                          ? "border-brand bg-brand/[0.06]"
                          : "border-border bg-surface pointer-fine:hover:bg-surface-alt",
                      )}
                    >
                      <StickerBlob sticker={sticker} size={58} />
                      <span className="line-clamp-1 w-full text-[12px] font-semibold text-text-primary">
                        {sticker.title}
                      </span>
                      <span className="line-clamp-1 w-full text-[10.5px] text-text-tertiary">
                        {stickerRequirement(sticker)}
                      </span>
                      {active ? (
                        <span className="absolute top-1.5 right-1.5 flex size-5 items-center justify-center rounded-full bg-brand text-text-inverse">
                          <Check className="size-3" strokeWidth={3} />
                        </span>
                      ) : null}
                    </button>
                  );
                })}
              </div>
              {lockedCount > 0 ? (
                <p className="pt-4 text-center text-[12px] text-text-tertiary">
                  {lockedCount} more to unlock in your{" "}
                  <BackAwareLink
                    href={notificationLinks.walletStickers()}
                    onClick={() => onOpenChange(false)}
                    className="font-semibold text-text-secondary underline-offset-2 hover:underline"
                  >
                    wallet
                  </BackAwareLink>
                </p>
              ) : null}
            </>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  );
}
